import { useState } from 'react';
import { renderMarkdown } from '@dia/content';
import { useT } from '@dia/i18n';
import { highlightPlaceholders } from '../logic/bilingual';
import type { GuideSection } from '../logic/guideSections';
import { ChevronIcon } from './icons';

const PROSE =
  'text-inkMuted leading-relaxed pb-4 flex flex-col gap-3 ' +
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-2 ' +
  '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:flex [&_ol]:flex-col [&_ol]:gap-2 ' +
  '[&_strong]:text-ink [&_strong]:font-medium ' +
  '[&_h3]:font-display [&_h3]:text-ink [&_h3]:text-[1.05em] [&_h3]:mt-1';

/**
 * Long pages (How the island works) as sections that open and close, Touch
 * Stay style (docs/GUEST-REDESIGN.md Section 5, item 6): the first one starts
 * open, and "Expand all" opens the rest. Reduced motion is a non issue here,
 * since a native `details` element has no animation to begin with.
 */
export function Accordion({ sections }: { sections: GuideSection[] }) {
  const { t } = useT();
  const [open, setOpen] = useState<Set<number>>(() => new Set(sections.length ? [0] : []));

  const allOpen = sections.length > 0 && open.size === sections.length;

  const toggleAll = () => {
    setOpen(allOpen ? new Set() : new Set(sections.map((_, i) => i)));
  };

  const toggleOne = (index: number, isOpen: boolean) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(index);
      else next.delete(index);
      return next;
    });
  };

  if (sections.length <= 1) {
    // Nothing to fold: one section reads better as plain prose. The caller
    // decides when to reach for the accordion at all (more than one heading).
    return null;
  }

  return (
    <div>
      <button
        type="button"
        onClick={toggleAll}
        className="mb-1 ml-auto block min-h-tap text-caption text-accent underline underline-offset-4"
      >
        {allOpen ? t('guest.guide.collapse_all') : t('guest.guide.expand_all')}
      </button>
      {sections.map((section, i) => (
        <details
          key={`${section.heading}-${i}`}
          open={open.has(i)}
          onToggle={(e) => toggleOne(i, e.currentTarget.open)}
          className="border-t border-line py-1 last:border-b"
        >
          <summary className="flex min-h-tap cursor-pointer list-none items-center justify-between gap-3 py-3 font-medium text-ink">
            {section.heading}
            <ChevronIcon
              className={`shrink-0 text-inkFaint transition-transform ${open.has(i) ? 'rotate-90' : ''}`}
              style={{ width: '1.1em', height: '1.1em' }}
            />
          </summary>
          <div
            className={PROSE}
            dangerouslySetInnerHTML={{ __html: highlightPlaceholders(renderMarkdown(section.body)) }}
          />
        </details>
      ))}
    </div>
  );
}
